import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, AlertTriangle, CheckCircle2, Save, Building2 } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { holdingApi, type FilialeLegal, type UpdateLegalMentionsPayload } from '@/api/holding';
import { extractError } from '@/api/client';
import { toast } from '@/stores/toastStore';

/**
 * Ticket SIMGROUP-2026-003 Sprint 1 — Édition des mentions légales OHADA
 * d'une filiale.
 *
 * Réservé à admin_holding. Bloque l'émission PDF (Sprint 2) si un champ
 * obligatoire est absent (rccm, ncc, siegeSocial, telephone, dirigeantNom,
 * formeJuridique).
 *
 * Route : /holding/filiales/:filialeKey/legal
 */
export const FilialeLegalPage = () => {
  const { filialeKey } = useParams<{ filialeKey: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['holding', 'filiale-legal', filialeKey],
    queryFn:  () => holdingApi.getFilialeLegal(filialeKey!),
    enabled:  !!filialeKey,
  });

  const [form, setForm] = useState<FilialeLegal | null>(null);
  useEffect(() => { if (data) setForm(data); }, [data]);

  const mutate = useMutation({
    mutationFn: (payload: UpdateLegalMentionsPayload) =>
      holdingApi.updateFilialeLegal(filialeKey!, payload),
    onSuccess: (updated) => {
      qc.setQueryData(['holding', 'filiale-legal', filialeKey], updated);
      qc.invalidateQueries({ queryKey: ['holding', 'filiales'] });
      toast.success('Mentions légales enregistrées', 'La fiche filiale a été mise à jour.');
    },
    onError: (err) => {
      toast.error('Échec de la mise à jour', extractError(err).message);
    },
  });

  if (isLoading || !form) {
    return (
      <>
        <Topbar title="Mentions légales" subtitle="Chargement…" />
        <div className="ds-content"><div style={{ padding: 32, textAlign: 'center', color: 'var(--tx-3)' }}>Chargement…</div></div>
      </>
    );
  }
  if (error) {
    return (
      <>
        <Topbar title="Mentions légales" subtitle="Erreur" />
        <div className="ds-content">
          <div className="ds-alert-strip ds-alert-d">
            <span className="ds-alert-ico"><AlertTriangle size={18} /></span>
            <div className="ds-alert-txt">{extractError(error).message}</div>
          </div>
        </div>
      </>
    );
  }

  const missing = form.missingLegal;
  const siege = form.siegeSocial ?? {};
  const patchSiege = (k: keyof typeof siege, v: string) =>
    setForm((p) => p ? { ...p, siegeSocial: { ...siege, [k]: v || undefined } } : p);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!form) return;
    mutate.mutate({
      formeJuridique:       form.formeJuridique      ?? null,
      capitalSocialCents:   form.capitalSocialCents  ?? null,
      rccm:                 form.rccm                ?? null,
      numeroContribuable:   form.numeroContribuable  ?? null,
      regimeTva:            form.regimeTva           ?? null,
      siegeSocial:          form.siegeSocial         ?? null,
      telephone:            form.telephone           ?? null,
      emailContact:         form.emailContact        ?? null,
      dirigeantNom:         form.dirigeantNom        ?? null,
      dirigeantTitre:       form.dirigeantTitre      ?? null,
      dirigeantSignatureUrl:form.dirigeantSignatureUrl ?? null,
    });
  };

  // Capital en FCFA (input converti en cents à la sauvegarde)
  const capitalFcfa = form.capitalSocialCents ? Number(BigInt(form.capitalSocialCents)) / 100 : '';

  return (
    <>
      <Topbar
        title={`Mentions légales · ${form.fullName}`}
        subtitle="Obligatoires OHADA sur toute pièce commerciale émise (proforma, facture, avoir)"
        actions={
          <>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => navigate('/holding/filiales')} type="button">
              <ArrowLeft size={14} /> Retour
            </button>
            <button className="ds-btn ds-btn-p" onClick={onSubmit} disabled={mutate.isPending} type="button">
              <Save size={14} /> {mutate.isPending ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </>
        }
      />

      <div className="ds-content">
        {missing.length > 0 ? (
          <div className="ds-alert-strip ds-alert-w" style={{ marginBottom: 16 }}>
            <span className="ds-alert-ico"><AlertTriangle size={18} /></span>
            <div className="ds-alert-txt">
              <strong>Champs obligatoires manquants ({missing.length})</strong> : {missing.join(', ')}.
              Ces champs bloqueront l'émission d'une facture opposable en OHADA tant qu'ils ne sont pas renseignés.
            </div>
          </div>
        ) : (
          <div className="ds-alert-strip ds-alert-s" style={{ marginBottom: 16 }}>
            <span className="ds-alert-ico"><CheckCircle2 size={18} /></span>
            <div className="ds-alert-txt">
              <strong>Fiche complète</strong> — toutes les mentions obligatoires OHADA sont renseignées.
              La filiale peut émettre des pièces commerciales opposables.
            </div>
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-4">
          {/* Section identité juridique */}
          <Section title="Identité juridique" icon={<Building2 size={14} />}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Forme juridique *" hint="SARL, SA, SUARL, EURL, SNC…">
                <input className="ds-fi" value={form.formeJuridique ?? ''}
                  onChange={(e) => setForm((p) => p ? { ...p, formeJuridique: e.target.value || null } : p)} />
              </Field>
              <Field label={`Capital social (${form.devise})`} hint="Montant en unités entières (pas de décimale)">
                <input className="ds-fi" type="number" min={0} step={1000} value={capitalFcfa}
                  onChange={(e) => setForm((p) => p ? { ...p, capitalSocialCents: e.target.value ? String(Math.round(parseFloat(e.target.value)) * 100) : null } : p)} />
              </Field>
              <Field label="RCCM *" hint="ex: CI-ABJ-03-2024-B12-08108">
                <input className="ds-fi" value={form.rccm ?? ''}
                  onChange={(e) => setForm((p) => p ? { ...p, rccm: e.target.value || null } : p)} />
              </Field>
              <Field label="N° Compte Contribuable (NCC) *" hint="ex: 9406175 M">
                <input className="ds-fi" value={form.numeroContribuable ?? ''}
                  onChange={(e) => setForm((p) => p ? { ...p, numeroContribuable: e.target.value || null } : p)} />
              </Field>
              <Field label="Régime TVA" hint='"Assujetti - 18%" ou "Non assujetti"'>
                <input className="ds-fi" value={form.regimeTva ?? ''}
                  onChange={(e) => setForm((p) => p ? { ...p, regimeTva: e.target.value || null } : p)} />
              </Field>
            </div>
          </Section>

          {/* Section siège social */}
          <Section title="Siège social *">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Adresse libre (quartier / rue)" hint="ex: Cocody Angré">
                <input className="ds-fi" value={siege.adresseLibre ?? ''} onChange={(e) => patchSiege('adresseLibre', e.target.value)} />
              </Field>
              <Field label="Ville" hint="ex: Abidjan">
                <input className="ds-fi" value={siege.ville ?? ''} onChange={(e) => patchSiege('ville', e.target.value)} />
              </Field>
              <Field label="Arrondissement" hint="ex: 22e Arrondissement">
                <input className="ds-fi" value={siege.arrondissement ?? ''} onChange={(e) => patchSiege('arrondissement', e.target.value)} />
              </Field>
              <div className="grid grid-cols-4 gap-2">
                <Field label="Lot"><input className="ds-fi" value={siege.lot ?? ''} onChange={(e) => patchSiege('lot', e.target.value)} /></Field>
                <Field label="Îlot"><input className="ds-fi" value={siege.ilot ?? ''} onChange={(e) => patchSiege('ilot', e.target.value)} /></Field>
                <Field label="Section"><input className="ds-fi" value={siege.section ?? ''} onChange={(e) => patchSiege('section', e.target.value)} /></Field>
                <Field label="Parcelle"><input className="ds-fi" value={siege.parcelle ?? ''} onChange={(e) => patchSiege('parcelle', e.target.value)} /></Field>
              </div>
            </div>
          </Section>

          {/* Section contact */}
          <Section title="Contact émetteur">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Téléphone *" hint="ex: +225 07 00 72 72 72">
                <input className="ds-fi" value={form.telephone ?? ''}
                  onChange={(e) => setForm((p) => p ? { ...p, telephone: e.target.value || null } : p)} />
              </Field>
              <Field label="Email contact" hint="Adresse affichée sur les pièces commerciales">
                <input className="ds-fi" type="email" value={form.emailContact ?? ''}
                  onChange={(e) => setForm((p) => p ? { ...p, emailContact: e.target.value || null } : p)} />
              </Field>
            </div>
          </Section>

          {/* Section dirigeant */}
          <Section title="Dirigeant inscrit au RCCM *">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Nom complet *" hint="Exactement comme inscrit au RCCM">
                <input className="ds-fi" value={form.dirigeantNom ?? ''}
                  onChange={(e) => setForm((p) => p ? { ...p, dirigeantNom: e.target.value || null } : p)} />
              </Field>
              <Field label="Titre" hint='ex: "La Gérante", "Le Directeur Général"'>
                <input className="ds-fi" value={form.dirigeantTitre ?? ''}
                  onChange={(e) => setForm((p) => p ? { ...p, dirigeantTitre: e.target.value || null } : p)} />
              </Field>
              <Field label="URL signature scannée" hint="PNG transparent 300×100 recommandé — à uploader via Supabase Storage (Sprint 2)">
                <input className="ds-fi" type="url" value={form.dirigeantSignatureUrl ?? ''}
                  onChange={(e) => setForm((p) => p ? { ...p, dirigeantSignatureUrl: e.target.value || null } : p)} />
              </Field>
            </div>
          </Section>
        </form>
      </div>
    </>
  );
};

const Section = ({ title, icon, children }: { title: string; icon?: React.ReactNode; children: React.ReactNode }) => (
  <div className="ds-panel" style={{ padding: 16 }}>
    <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
      {icon} {title}
    </h3>
    {children}
  </div>
);

const Field = ({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) => (
  <div>
    <label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>
    {children}
    {hint && <div className="mt-1 text-[10px] text-gray-400">{hint}</div>}
  </div>
);
